import classNames from "classnames";
import React, { type Ref, useImperativeHandle, useRef, useState } from "react";
import "./Button.css";
import LoadingSpinner from "./LoadingSpinner";
import { NewButton } from "./NewButton";

type Props = {
    className?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    label?: string;
    selected?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">;

function BaseButton(baseClassName?: string) {
    return React.forwardRef((props: Props, ref: Ref<HTMLButtonElement>) => {
        const { selected, type, className, children, disabled, label, onClick } = props;

        const buttonRef = useRef<HTMLButtonElement>(null);
        useImperativeHandle(ref, () => {
            if (!buttonRef.current) {
                throw new Error("Button ref is null - component not properly mounted");
            }
            return buttonRef.current;
        });
        const [isLoading, setIsLoading] = useState<boolean>(false);

        const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (disabled || isLoading) return;

            if (onClick) {
                setIsLoading(true);
                try {
                    await onClick(e);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        return (
            <button
                ref={buttonRef}
                type={type || "button"}
                className={classNames(
                    "web-lib__button-base",
                    baseClassName,
                    { "web-lib__button-pill--selected": selected },
                    className,
                )}
                disabled={disabled || isLoading}
                onClick={handleClick}
                aria-busy={isLoading}
                aria-disabled={disabled || isLoading}
            >
                {isLoading ? (
                    <span
                        className="web-lib__button__spinner-wrapper"
                        aria-hidden="true"
                    >
                        <LoadingSpinner className="web-lib__button__spinner" />
                    </span>
                ) : (
                    <span>
                        {label}
                        {children}
                    </span>
                )}
            </button>
        );
    });
}

const Button = Object.assign(BaseButton("web-lib__button"), {
    /** @deprecated Use @albertine/design-system/components/Button variant="primary" instead */
    Primary: BaseButton("web-lib__button-primary"),
    /** @deprecated Use @albertine/design-system/components/Button variant="inverse" instead */
    Inverse: BaseButton("web-lib__button-inverse"),
    Ghost: BaseButton("web-lib__button-ghost"),
    /** @deprecated Use @albertine/design-system/components/Button variant="onboarding" instead */
    Onboarding: BaseButton("web-lib__button-onboarding"),
    /** @deprecated Use @albertine/design-system/components/ToggleButton instead */
    Pill: BaseButton("web-lib__button-pill"),
    /** @deprecated Use @albertine/design-system/components/Button variant="secondary" instead */
    Secondary: BaseButton("web-lib__button-secondary"),
    /** @deprecated Compatibility only. Use @albertine/design-system/components/Button instead. */
    New: NewButton,
});

export default Button;
